import { useState, useEffect, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Activity, 
  Users, 
  FileText, 
  Video, 
  TrendingUp, 
  Clock,
  Zap,
  ArrowRight,
  Loader2,
  Bookmark,
  Share2,
  ChevronRight,
  Brain,
  Bell,
  AlertTriangle,
  Target,
  Sparkles,
  BarChart3,
  RefreshCw,
  Play,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import SmartAlertsWidget from "../components/dashboard/SmartAlertsWidget";
import { Skeleton, SkeletonCard, SkeletonList } from "../components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { darkMode, region, brand } = useOutletContext() || { darkMode: true, region: 'UAE', brand: 'blackswan' };
  const [stats, setStats] = useState(null);
  const [latestIntel, setLatestIntel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningAnalysis, setRunningAnalysis] = useState(false);
  const navigate = useNavigate();

  // New state for advanced features
  const [activityFeed, setActivityFeed] = useState([]);
  const [smartAlerts, setSmartAlerts] = useState([]);
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [predictiveData, setPredictiveData] = useState(null);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  const [activityExpanded, setActivityExpanded] = useState(true);
  const [alertsExpanded, setAlertsExpanded] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, intelRes, activityRes, alertsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats?region=${region}&brand=${brand}`),
        axios.get(`${API}/intelligence/latest?region=${region}&brand=${brand}`),
        axios.get(`${API}/analytics/activity-feed/${brand}/${region}`),
        axios.get(`${API}/analytics/smart-alerts/${brand}/${region}`)
      ]);
      setStats(statsRes.data);
      setLatestIntel(intelRes.data);
      setActivityFeed(activityRes.data.activities || []);
      setSmartAlerts(alertsRes.data.alerts || []);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  }, [region, brand]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData, region]);

  const triggerIntelligenceRun = async () => {
    setRunningAnalysis(true);
    try {
      await axios.post(`${API}/intelligence/run`, { region, brand });
      toast.success(`Intelligence scan started for ${region}`);
      const pollInterval = setInterval(async () => {
        const res = await axios.get(`${API}/intelligence/latest?region=${region}&brand=${brand}`);
        setLatestIntel(res.data);
        if (res.data?.report?.status === "completed" || res.data?.report?.status === "failed") {
          clearInterval(pollInterval);
          setRunningAnalysis(false);
          fetchData();
          toast.success(res.data?.report?.status === "completed" ? "Scan complete!" : "Scan failed");
        }
      }, 5000);
    } catch (error) {
      setRunningAnalysis(false);
      toast.error("Failed to start scan");
    }
  };

  const generateAIRecommendations = async () => {
    setLoadingRecommendations(true);
    try {
      const res = await axios.post(`${API}/analytics/ai-content-recommendations/${brand}/${region}`);
      setAiRecommendations(res.data.recommendations);
      toast.success("AI recommendations generated!");
    } catch (error) {
      toast.error("Failed to generate recommendations");
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const loadPredictiveAnalytics = async () => {
    setLoadingPredictions(true);
    try {
      const res = await axios.get(`${API}/analytics/predictive/${brand}/${region}`);
      setPredictiveData(res.data.predictions);
      toast.success("Predictive analytics loaded!");
    } catch (error) {
      toast.error("Failed to load predictions");
    } finally {
      setLoadingPredictions(false);
    }
  };

  // Activity icon mapping
  const getActivityIcon = (type) => {
    switch(type) {
      case 'intelligence_scan': return Activity;
      case 'content_created': return FileText;
      case 'video_generated': return Video;
      case 'social_scraped': return Share2;
      default: return Activity;
    }
  };

  // Alert severity colors
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto space-y-6" data-testid="dashboard-loading">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between animate-fadeIn">
          <div>
            <Skeleton className={`h-8 w-24 mb-2 ${darkMode ? 'bg-[#21262d]' : ''}`} />
            <Skeleton className={`h-4 w-48 ${darkMode ? 'bg-[#21262d]' : ''}`} />
          </div>
          <Skeleton className={`h-10 w-48 rounded-lg ${darkMode ? 'bg-[#21262d]' : ''}`} />
        </div>
        
        {/* Metrics Grid Skeleton */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={`animate-fadeIn stagger-${i}`}>
              <SkeletonCard darkMode={darkMode} />
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content Skeleton */}
          <div className={`lg:col-span-2 rounded-xl overflow-hidden animate-fadeIn stagger-5 ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
            <div className={`px-4 py-3 border-b flex justify-between ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
              <Skeleton className={`h-5 w-32 ${darkMode ? 'bg-[#21262d]' : ''}`} />
              <Skeleton className={`h-4 w-16 ${darkMode ? 'bg-[#21262d]' : ''}`} />
            </div>
            <SkeletonList count={4} darkMode={darkMode} className="p-4" />
          </div>
          
          {/* Sidebar Skeleton */}
          <div className="space-y-4 animate-fadeIn stagger-6">
            <SkeletonCard darkMode={darkMode} className="h-48" />
            <SkeletonCard darkMode={darkMode} className="h-64" />
          </div>
        </div>
      </div>
    );
  }

  const metrics = [
    { label: "Competitors", value: stats?.competitor_count || 0, icon: Users, color: "text-[#DC143C]", bg: "bg-[#DC143C]/10" },
    { label: "Reports", value: stats?.report_count || 0, icon: Activity, color: "text-blue-500", bg: "bg-blue-500/10" },
    { label: "Content", value: stats?.content_count || 0, icon: FileText, color: "text-emerald-500", bg: "bg-emerald-500/10" },
    { label: "Videos", value: stats?.video_count || 0, icon: Video, color: "text-purple-500", bg: "bg-purple-500/10" },
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Today
          </h1>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <Button
          onClick={triggerIntelligenceRun}
          disabled={runningAnalysis}
          className="bg-[#DC143C] hover:bg-[#b91031] text-white"
          data-testid="run-intelligence-btn"
        >
          {runningAnalysis ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Scanning...
            </>
          ) : (
            <>
              <Zap className="w-4 h-4 mr-2" />
              Run Intelligence Scan
            </>
          )}
        </Button>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((metric, index) => (
          <div 
            key={metric.label}
            className={`rounded-xl p-5 animate-fadeIn stagger-${index + 1} ${
              darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'
            }`}
            data-testid={`metric-${metric.label.toLowerCase()}`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {metric.label}
              </span>
              <div className={`p-2 rounded-lg ${metric.bg}`}>
                <metric.icon className={`w-4 h-4 ${metric.color}`} />
              </div>
            </div>
            <p className={`text-3xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {metric.value}
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Latest Insights - Feedly Feed Style */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Latest Insights
            </h2>
            <button 
              onClick={() => navigate('/intelligence')}
              className={`text-sm flex items-center gap-1 ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
            >
              View all <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
            {latestIntel?.report?.top_insights?.length > 0 ? (
              latestIntel.report.top_insights.slice(0, 5).map((insight, index) => (
                <div 
                  key={insight.id || insight.topic || `insight-${index}`}
                  className={`p-4 flex gap-4 cursor-pointer transition-colors ${
                    index !== 0 && (darkMode ? 'border-t border-[#21262d]' : 'border-t border-gray-100')
                  } ${darkMode ? 'hover:bg-[#21262d]' : 'hover:bg-gray-50'}`}
                  data-testid={`insight-${index}`}
                >
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className="bg-[#DC143C]/10 text-[#DC143C] border-0 text-xs">
                        {insight.topic_category}
                      </Badge>
                      {insight.emotional_trigger && (
                        <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          • {insight.emotional_trigger}
                        </span>
                      )}
                    </div>
                    <h3 className={`font-medium mb-1.5 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {insight.topic}
                    </h3>
                    <p className={`text-sm line-clamp-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {insight.reason_for_traction || insight.business_implication}
                    </p>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-start gap-1">
                    <button className={`p-2 rounded-lg ${darkMode ? 'hover:bg-[#30363d] text-gray-400' : 'hover:bg-gray-100 text-gray-400'}`}>
                      <Bookmark className="w-4 h-4" />
                    </button>
                    <button className={`p-2 rounded-lg ${darkMode ? 'hover:bg-[#30363d] text-gray-400' : 'hover:bg-gray-100 text-gray-400'}`}>
                      <Share2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className={`p-12 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No insights yet</p>
                <p className="text-sm mt-1">Run an intelligence scan to generate insights</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Status & Quick Actions */}
        <div className="space-y-4">
          {/* Latest Report Status */}
          {stats?.latest_report && (
            <div className={`rounded-xl p-5 ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Latest Scan
                </h3>
                <Badge className={`
                  ${stats.latest_report.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : ''}
                  ${stats.latest_report.status === 'running' ? 'bg-blue-100 text-blue-700' : ''}
                  ${stats.latest_report.status === 'failed' ? 'bg-red-100 text-red-700' : ''}
                  ${stats.latest_report.status === 'pending' ? 'bg-amber-100 text-amber-700' : ''}
                  border-0
                `}>
                  {stats.latest_report.status}
                </Badge>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Competitors</span>
                  <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {stats.latest_report.competitor_count}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Type</span>
                  <span className={`text-sm font-medium capitalize ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {stats.latest_report.run_type}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Completed</span>
                  <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {stats.latest_report.completed_at 
                      ? new Date(stats.latest_report.completed_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                      : 'In progress'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
            <div className={`px-5 py-4 border-b ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
              <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Quick Actions
              </h3>
            </div>
            <div className="p-2">
              {[
                { icon: Users, label: "Add Competitor", path: "/competitors", color: "text-[#DC143C]" },
                { icon: FileText, label: "View Content", path: "/content", color: "text-emerald-500" },
                { icon: Video, label: "Video Studio", path: "/videos", color: "text-purple-500" },
                { icon: Brain, label: "View Insights", path: "/learning", color: "text-blue-500" },
              ].map((action) => (
                <button
                  key={action.label}
                  onClick={() => navigate(action.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    darkMode ? 'hover:bg-[#21262d] text-gray-300' : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <action.icon className={`w-5 h-5 ${action.color}`} />
                  <span className="text-sm font-medium">{action.label}</span>
                  <ArrowRight className={`w-4 h-4 ml-auto ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                </button>
              ))}
            </div>
          </div>

          {/* Smart Alerts Widget */}
          <SmartAlertsWidget
            darkMode={darkMode}
            smartAlerts={smartAlerts}
            alertsExpanded={alertsExpanded}
            setAlertsExpanded={setAlertsExpanded}
            triggerIntelligenceRun={triggerIntelligenceRun}
            navigate={navigate}
          />

          {/* AI Recommendations Widget */}
          <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
            <div className={`px-5 py-4 border-b ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    AI Content Ideas
                  </h3>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={generateAIRecommendations}
                  disabled={loadingRecommendations}
                  className="h-7 text-xs"
                  data-testid="generate-recommendations-btn"
                >
                  {loadingRecommendations ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3 h-3" />
                  )}
                </Button>
              </div>
            </div>
            <div className="p-3 space-y-2 max-h-64 overflow-y-auto">
              {aiRecommendations?.recommendations?.slice(0, 3).map((rec, idx) => (
                <div
                  key={rec.id || rec.topic || `rec-${idx}`}
                  className={`p-3 rounded-lg ${darkMode ? 'bg-[#21262d] hover:bg-[#30363d]' : 'bg-gray-50 hover:bg-gray-100'} cursor-pointer transition-colors`}
                >
                  <div className="flex items-start gap-2">
                    <Target className="w-4 h-4 text-[#DC143C] mt-0.5" />
                    <div>
                      <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {rec.topic}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={`text-xs border-0 ${
                          rec.priority === 'high' ? 'bg-red-500/10 text-red-500' :
                          rec.priority === 'medium' ? 'bg-amber-500/10 text-amber-500' :
                          'bg-blue-500/10 text-blue-500'
                        }`}>
                          {rec.priority}
                        </Badge>
                        <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {rec.category}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )) || (
                <div className={`text-center py-6 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Click refresh to generate AI content ideas</p>
                </div>
              )}
            </div>
          </div>

          {/* Predictive Analytics Widget */}
          <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
            <div className={`px-5 py-4 border-b ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-purple-500" />
                  <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Predictive Insights
                  </h3>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={loadPredictiveAnalytics}
                  disabled={loadingPredictions}
                  className="h-7 text-xs"
                  data-testid="load-predictions-btn"
                >
                  {loadingPredictions ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3 h-3" />
                  )}
                </Button>
              </div>
            </div>
            <div className="p-3 space-y-3">
              {predictiveData ? (
                <>
                  {predictiveData.weekly_forecast && (
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                      <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Weekly Forecast
                      </p>
                      <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Trend: <span className={`font-medium ${
                          predictiveData.weekly_forecast.expected_engagement_trend === 'increasing' ? 'text-emerald-500' :
                          predictiveData.weekly_forecast.expected_engagement_trend === 'decreasing' ? 'text-red-500' :
                          'text-amber-500'
                        }`}>
                          {predictiveData.weekly_forecast.expected_engagement_trend}
                        </span>
                      </p>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Focus: {predictiveData.weekly_forecast.recommended_focus}
                      </p>
                    </div>
                  )}
                  {predictiveData.trending_topics?.slice(0, 2).map((topic) => (
                    <div key={topic.topic} className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-emerald-500" />
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {topic.topic}
                      </span>
                    </div>
                  ))}
                </>
              ) : (
                <div className={`text-center py-6 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Click refresh for AI predictions</p>
                </div>
              )}
            </div>
          </div>

          {/* Pro Tip */}
          <div className={`rounded-xl p-5 ${darkMode ? 'bg-[#DC143C]/10 border border-[#DC143C]/20' : 'bg-[#DC143C]/5 border border-[#DC143C]/10'}`}>
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-[#DC143C]/20">
                <TrendingUp className="w-4 h-4 text-[#DC143C]" />
              </div>
              <div>
                <h4 className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Pro Tip
                </h4>
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Add more competitors to get broader market insights. Track up to 20 competitors.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Real-time Activity Feed - Full Width */}
      <Collapsible open={activityExpanded} onOpenChange={setActivityExpanded}>
        <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
          <CollapsibleTrigger className="w-full">
            <div className={`px-5 py-4 border-b flex items-center justify-between cursor-pointer ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-emerald-500" />
                <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Activity Feed
                </h3>
                <Badge className="bg-emerald-500/10 text-emerald-500 border-0 text-xs">Live</Badge>
              </div>
              {activityExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="divide-y divide-[#21262d]" data-testid="activity-feed">
              {activityFeed.slice(0, 10).map((activity, idx) => {
                const IconComponent = getActivityIcon(activity.type);
                return (
                  <div
                    key={activity.id || `${activity.type}-${activity.timestamp || idx}`}
                    className={`p-4 flex items-start gap-3 ${darkMode ? 'hover:bg-[#21262d]' : 'hover:bg-gray-50'} transition-colors`}
                  >
                    <div className={`p-2 rounded-lg ${
                      activity.type === 'intelligence_scan' ? 'bg-blue-500/10' :
                      activity.type === 'content_created' ? 'bg-emerald-500/10' :
                      activity.type === 'video_generated' ? 'bg-purple-500/10' :
                      'bg-gray-500/10'
                    }`}>
                      <IconComponent className={`w-4 h-4 ${
                        activity.type === 'intelligence_scan' ? 'text-blue-500' :
                        activity.type === 'content_created' ? 'text-emerald-500' :
                        activity.type === 'video_generated' ? 'text-purple-500' :
                        'text-gray-500'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {activity.title}
                        </p>
                        {activity.status === 'completed' && (
                          <CheckCircle className="w-3 h-3 text-emerald-500" />
                        )}
                        {activity.status === 'failed' && (
                          <XCircle className="w-3 h-3 text-red-500" />
                        )}
                        {activity.status === 'pending' && (
                          <Clock className="w-3 h-3 text-amber-500" />
                        )}
                      </div>
                      <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {activity.description}
                      </p>
                    </div>
                    <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {activity.timestamp ? new Date(activity.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                );
              })}
              {activityFeed.length === 0 && (
                <div className={`p-8 text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No recent activity</p>
                </div>
              )}
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </div>
  );
}
